import { cloneDeep, toInteger } from 'lodash';
import tinycolor from 'tinycolor2';
import {
    bgVirtualBridge,
    complexBridge,
    createCanvas as createCanvasWasm,
    filterBridge,
    fixZipResUrl,
    getStripArea,
    imageKit,
    MTIKProjectionType,
} from '@meitu/wasm-lib';
import assetsConfig from '@meitu/wasm-lib/assetsConfig.json';
import type {
    MTIKComplexGlowConfig,
    MTIKComplexProjectionConfig,
    MTIKComplexShadowConfig,
    MTIKComplexStrokeConfig,
} from '@meitu/wasm-lib/typings/lib/define/complexDefine';
import { base64Kit } from '@/utils/base64-kit';
import { createCanvas, isMd5Uri, loadImageAsync } from '@/utils/file-kit';
import { isEmptyEffect } from '@/utils/layer-kit';
import { uploadBase64ToObs } from '@/utils/upload-kit/obs-manager';
import { IImageEffectShadowType, LayerType, type IImageLayerProps, type ILayerProps } from '@/version-types';
/**
 * 图片特效 WASM 运行时
 *
 * 负责串行执行特效和模糊任务，不依赖图片特效插件实例或交互状态。
 */
import { ComplexBridgeFilterID, getImageEffectResultImgSize } from './constants';
import { getEffectParams } from './effect-params';
import { getEffectRenderSize } from './effect-render-size';

export { getEffectParams } from './effect-params';

const GlassBlurFilterID = 'Uglass';

const ClassicBlurFilterID = 'classBlur';

/** 图片特效 WASM AR 资源标识。 */
export const AR_RES_ID = 'ar_res';

let isLoadedAssets = false;
let complexRenderQueue: Promise<void> = Promise.resolve();
let complexBridgeRevision = 0;
let complexBridgeOwner: symbol | null = null;
let complexRenderSize: {
    layerId: string;
    width: number;
    height: number;
    expansion: number;
    size: ReturnType<typeof getEffectRenderSize>;
} | null = null;

const getBoundRenderSize = (layer: ILayerProps) =>
    complexRenderSize?.layerId === layer.id &&
    complexRenderSize.width === layer.width &&
    complexRenderSize.height === layer.height &&
    complexRenderSize.expansion === getImageEffectResultImgSize(layer.data as IImageLayerProps)
        ? complexRenderSize.size
        : undefined;

const enqueueComplexRender = <T>(task: () => Promise<T>) => {
    const result = complexRenderQueue.catch(() => undefined).then(task);
    complexRenderQueue = result.then(
        () => undefined,
        () => undefined,
    );
    return result;
};

/** 图片基础特效 GL 预览租约。 */
export interface IImageEffectPreviewLease {
    /** 预览会话唯一标识。 */
    token: symbol;
    /** 目标图片图层 ID。 */
    layerId: string;
    /** 初始化 complexBridge 使用的 URL + mask 基础画布。 */
    baseCanvas: HTMLCanvasElement;
    /** 租约最后一次绑定的全局 bridge 代次。 */
    bridgeRevision: number;
}

/** 图片基础特效 GL 预览结果。 */
export interface IImageEffectPreviewResult {
    /** 更新 bridge 代次后的预览租约。 */
    lease: IImageEffectPreviewLease;
    /** 与全局 bridge 解耦的独立 canvas 快照。 */
    canvas: HTMLCanvasElement;
    /** 当前特效外扩位置。 */
    effectPosition?: NonNullable<IImageLayerProps['effect']>['effectPosition'];
}

/** 基于指定图片本体重新计算的图片特效快照。 */
export interface IImageEffectCanvasResult {
    /** 与全局 complexBridge 解耦的独立 canvas 快照。 */
    canvas: HTMLCanvasElement;
    /** 当前特效外扩位置。 */
    effectPosition?: NonNullable<IImageLayerProps['effect']>['effectPosition'];
}

const copyCanvas = (canvas: HTMLCanvasElement) => createCanvas(canvas.width, canvas.height, canvas).canvas;

/**
 * 获取图片图层图片然后再外扩三倍
 *
 * @param layer - 图层属性
 * @param layerResultImage - 图层结果图片
 * @param imgEffectResultImgSize - 特效结果画布相对原图的扩展倍数
 * @param renderSize - 受纹理上限约束的工作画布尺寸
 * @returns HTMLCanvasElement
 */
export const getImageLayerScaleCanvas = async (
    layer: ILayerProps,
    layerResultImage: HTMLCanvasElement,
    imgEffectResultImgSize: number,
    renderSize?: ReturnType<typeof getEffectRenderSize>,
) => {
    if (!layerResultImage) {
        return;
    }
    const originWidth = layer.width;
    const originHeight = layer.height;

    const scaleCanvas = document.createElement('canvas');
    scaleCanvas.width = renderSize?.canvasWidth ?? originWidth * imgEffectResultImgSize;
    scaleCanvas.height = renderSize?.canvasHeight ?? originHeight * imgEffectResultImgSize;
    const scale = renderSize?.scale ?? 1;
    const renderWidth = originWidth * scale;
    const renderHeight = originHeight * scale;

    const ctx = scaleCanvas.getContext('2d');
    ctx?.drawImage(
        layerResultImage,
        0,
        0,
        layerResultImage.width,
        layerResultImage.height,
        (scaleCanvas.width - renderWidth) / 2,
        (scaleCanvas.height - renderHeight) / 2,
        renderWidth,
        renderHeight,
    );

    return scaleCanvas;
};

/** WASM complexBridge 使用的空特效配置。 */
export const EmptyEffect = [
    {
        mComplexType: 2,
        mColorR: 1,
        mColorG: 1,
        mColorB: 1,
        mColorA: 1,
        mEditColorEnable: false,
        mEditSizeEnable: false,
        mEnable: false,
        mLayerStyle: 0,
        mSize: 0.18,
    },
    {
        mComplexType: 1,
        mBlur: 0,
        mColorR: 0,
        mColorG: 0,
        mColorB: 0,
        mColorA: 0,
        mEditColorEnable: false,
        mEditSizeEnable: false,
        mEnable: false,
        mLayerStyle: 6,
    },
    {
        mComplexType: 0,
        mBlur: 0.6,
        mColorR: 0,
        mColorG: 0,
        mColorB: 0,
        mColorA: 0.4980392156862745,
        mEditColorEnable: false,
        mEditSizeEnable: false,
        mEnable: false,
        mLayerStyle: 7,
        mOffsetX: 0,
        mOffsetY: 0,
    },
];

class ImageEffectAction {
    constructor() {}
    static isEmptyEffect(layerData: IImageLayerProps) {
        return isEmptyEffect(layerData);
    }

    static glRender(layer: ILayerProps, layerResultImage: HTMLCanvasElement, noDisplayView = false) {
        return enqueueComplexRender(() => ImageEffectAction.glRenderUnsafe(layer, layerResultImage, noDisplayView));
    }

    private static async glRenderUnsafe(
        layer: ILayerProps,
        layerResultImage: HTMLCanvasElement,
        noDisplayView = false,
    ) {
        if (!layer) return false;

        if (layer.layerType !== LayerType.IMAGE_LAYER) {
            return false;
        }

        const imgEffectResultImgSize = getImageEffectResultImgSize(layer.data as IImageLayerProps);
        // 先由 WASM 创建自己的 GL 上下文，避免查询上限时抢先创建不同类型的上下文。
        await complexBridge.getManager();
        const glCanvas = imageKit.getGLCanvas();
        const gl = glCanvas?.getContext('webgl2') || glCanvas?.getContext('webgl');
        if (!gl) throw new Error('Image effect WebGL context is unavailable');
        const textureLimit = Math.min(gl.getParameter(gl.MAX_TEXTURE_SIZE), gl.getParameter(gl.MAX_RENDERBUFFER_SIZE));
        const renderSize = getEffectRenderSize(layer.width, layer.height, imgEffectResultImgSize, textureLimit);
        const scaleCanvas = (await getImageLayerScaleCanvas(
            layer,
            layerResultImage,
            imgEffectResultImgSize,
            renderSize,
        )) as HTMLCanvasElement;
        complexRenderSize = null;

        await complexBridge
            .from(scaleCanvas)
            .apply(
                ComplexBridgeFilterID,
                fixZipResUrl(assetsConfig.complex_multiConfig),
                1,
                'configuration.plist',
                fixZipResUrl(assetsConfig.complex_SmearRes),
                false,
                false,
            );

        complexRenderSize = {
            layerId: layer.id,
            width: layer.width,
            height: layer.height,
            expansion: imgEffectResultImgSize,
            size: renderSize,
        };
        // complexBridge 是全局单例，重新绑定输入后必须使旧预览租约失效。
        complexBridgeRevision += 1;
        complexBridgeOwner = null;

        const stripArea = getStripArea(layerResultImage);
        const bottomPadding = Math.max(
            0,
            (scaleCanvas.height - layer.height * imgEffectResultImgSize * renderSize.scale) / 2,
        );
        const sourceBottom =
            ((layerResultImage.height - stripArea.y - stripArea.height) * layer.height) / layerResultImage.height;
        const ratio = (sourceBottom * renderSize.scale + bottomPadding) / scaleCanvas.height;
        complexBridge.setBottomOffset(ratio);
        await ImageEffectAction.processRenderUnsafe(layer, true, noDisplayView);

        return false;
    }

    static async getEffectPosition(layer: ILayerProps) {
        const { width, height } = layer;
        const size = getBoundRenderSize(layer);
        if (!size?.adjusted) return complexBridge.sticker2ExpandPosition(0, 0, width, height);
        const position = complexBridge.sticker2ExpandPosition(0, 0, size.virtualWidth, size.virtualHeight);
        return {
            left: (position.left - size.offsetX) / size.scale,
            right: (position.right - size.offsetX) / size.scale,
            top: (position.top - size.offsetY) / size.scale,
            bottom: (position.bottom - size.offsetY) / size.scale,
        };
    }

    static processRender(layer: ILayerProps, isUpdateBorderRadius = true, noDisplayView = false) {
        return enqueueComplexRender(async () => {
            const result = await ImageEffectAction.processRenderUnsafe(layer, isUpdateBorderRadius, noDisplayView);
            // 非租约调用修改了全局参数，旧租约下次更新时必须重新绑定自己的底图。
            complexBridgeRevision += 1;
            complexBridgeOwner = null;
            return result;
        });
    }

    private static async processRenderUnsafe(layer: ILayerProps, isUpdateBorderRadius = true, noDisplayView = false) {
        const imgEffectResultImgSize = getImageEffectResultImgSize(layer.data as IImageLayerProps);
        complexBridge.setIsEnableBlend(false);
        complexBridge.setExpandCanvasFactor(imgEffectResultImgSize);
        complexBridge.setCanvasExpandRatio(imgEffectResultImgSize);
        const renderSize = getBoundRenderSize(layer);
        // 每次设置，防止细长图层的参数补偿污染下一个普通图层或预览租约。
        complexBridge.setParamsScale(renderSize?.paramsScale ?? 1);
        const lowLevelData = ImageEffectAction.transferLayerToLowLevelData(layer);

        if (isUpdateBorderRadius) {
            complexBridge.setIsDrawRound(true);
            complexBridge.setIsDrawSdf(true);
            const { borderRadius } = layer.data as IImageLayerProps;
            if (!borderRadius || borderRadius.hidden) {
                complexBridge.setRoundRadius(0, 0, 0, 0);
            } else if (borderRadius && !borderRadius.hidden) {
                const size = (Math.min(layer.width, layer.height) * (renderSize?.scale ?? 1)) / 2;
                complexBridge.setRoundRadius(
                    borderRadius.leftTop * size * 0.01,
                    borderRadius.leftBottom * size * 0.01,
                    borderRadius.rightTop * size * 0.01,
                    borderRadius.rightBottom * size * 0.01,
                );
            }
        }
        complexBridge.setEnableTotalControl(false);
        complexBridge.setComplexFilterConfigArray(lowLevelData as any);

        await complexBridge.processRender();

        const position = await ImageEffectAction.getEffectPosition(layer);
        if ((layer.data as IImageLayerProps)?.effect)
            (layer.data as IImageLayerProps).effect!.effectPosition = position;
        // console.log(
        //     'position ===>',
        //     position.right - position.left,
        //     position.bottom - position.top,
        //     layer.width,
        //     layer.height,
        //     layer.width * 1.4,
        //     layer.height * 1.4,
        // );
        if (!noDisplayView) {
            await ImageEffectAction.displayView(layer);
        }

        complexBridge.setIsNeedUpdateSdf(true);
    }

    static async displayView(layer: ILayerProps) {
        const container = document.getElementById(`${layer.id}GLContainer`);
        const position = (layer.data as IImageLayerProps)?.effect?.effectPosition;
        if (!container) return;
        const { canvas } = await complexBridge.getGLResult();
        if (!canvas) return;
        if (container.children.length > 0) container.removeChild(container?.children[0]);
        container.appendChild(canvas!);
        if (!position) return;
        canvas.style.width = container.style.width = position.right - position.left + 'px';
        canvas.style.height = container.style.height = position.bottom - position.top + 'px';
        container.style.transform = `translate(${position.left}px, ${position.top}px)`;
    }

    static getScreenshotUrl(layer: ILayerProps, base64 = false) {
        return enqueueComplexRender(() => ImageEffectAction.getScreenshotUrlUnsafe(layer, base64));
    }

    private static async getScreenshotUrlUnsafe(layer: ILayerProps, base64 = false) {
        if (base64) {
            const result = await complexBridge.getGLResult();
            const resUrl = result.canvas?.toDataURL();
            return resUrl;
        }
        const params = getEffectParams(layer);
        if (!params) return null;
        const code = base64Kit.md5(params);
        const localUrl = base64Kit.getLocalUrl(code);
        if (!isMd5Uri(localUrl)) {
            return code;
        } else {
            const result = await complexBridge.getGLResult();
            const resUrl = result.canvas?.toDataURL();
            return base64Kit.getCode(resUrl!, params);
        }
    }

    static runEffectAndGetUrl(layer: ILayerProps, layerResultImage: HTMLCanvasElement, base64 = false) {
        return enqueueComplexRender(async () => {
            await ImageEffectAction.glRenderUnsafe(layer, layerResultImage, true);
            return ImageEffectAction.getScreenshotUrlUnsafe(layer, base64);
        });
    }

    /**
     * 基于指定图片本体重新计算图片特效并返回独立 Canvas
     *
     * 用于滤镜等只修改图片本体的临时预览；不会生成 base64，也不会写入正式图层的特效派生字段。
     *
     * @param layer - 当前图片图层快照
     * @param layerResultImage - 已完成图片本体变换的 Canvas
     * @returns 特效 Canvas 快照和外扩位置
     *
     * 副作用：串行占用并更新全局 complexBridge。
     */
    static runEffectAndGetCanvas(
        layer: ILayerProps,
        layerResultImage: HTMLCanvasElement,
    ): Promise<IImageEffectCanvasResult> {
        const stableBaseCanvas = copyCanvas(layerResultImage);
        return enqueueComplexRender(async () => {
            const renderLayer = cloneDeep(layer);
            await ImageEffectAction.glRenderUnsafe(renderLayer, stableBaseCanvas, true);
            const { canvas } = await complexBridge.getGLResult();
            if (!canvas) {
                throw new Error(`Image effect preview result is empty: ${layer.id}`);
            }

            return {
                canvas: copyCanvas(canvas),
                effectPosition: cloneDeep((renderLayer.data as IImageLayerProps).effect?.effectPosition),
            };
        });
    }

    static processEffectAndGetUrl(
        layer: ILayerProps,
        isUpdateBorderRadius = true,
        base64 = false,
        noDisplayView = false,
    ) {
        return enqueueComplexRender(async () => {
            await ImageEffectAction.processRenderUnsafe(layer, isUpdateBorderRadius, noDisplayView);
            complexBridgeRevision += 1;
            complexBridgeOwner = null;
            return ImageEffectAction.getScreenshotUrlUnsafe(layer, base64);
        });
    }

    /**
     * 创建图片基础特效 GL 预览租约
     *
     * 使用 URL + mask 的基础画布初始化 complexBridge，并返回不依赖全局可变 canvas 的快照。
     * 副作用：占用并更新全局 complexBridge。
     *
     * @param layer - 当前图片特效草稿
     * @param baseCanvas - URL + mask 合成后的基础画布
     * @param token - 预览会话唯一标识
     * @returns 预览租约与首帧 canvas 快照
     */
    static createPreviewLease(
        layer: ILayerProps,
        baseCanvas: HTMLCanvasElement,
        token: symbol,
    ): Promise<IImageEffectPreviewResult> {
        const stableBaseCanvas = copyCanvas(baseCanvas);
        return enqueueComplexRender(async () => {
            const renderLayer = cloneDeep(layer);
            await ImageEffectAction.glRenderUnsafe(renderLayer, stableBaseCanvas, true);
            complexBridgeOwner = token;

            const { canvas } = await complexBridge.getGLResult();
            if (!canvas) {
                throw new Error(`Image effect preview result is empty: ${layer.id}`);
            }

            return {
                lease: {
                    token,
                    layerId: layer.id,
                    baseCanvas: stableBaseCanvas,
                    bridgeRevision: complexBridgeRevision,
                },
                canvas: copyCanvas(canvas),
                effectPosition: cloneDeep((renderLayer.data as IImageLayerProps).effect?.effectPosition),
            };
        });
    }

    /**
     * 更新图片基础特效 GL 预览租约
     *
     * bridge 仍归当前租约所有时只更新参数；被其他任务占用后先重新绑定租约底图。
     * 副作用：更新全局 complexBridge 参数和预览 canvas。
     *
     * @param lease - 当前预览租约
     * @param layer - 最新图片特效草稿
     * @returns 更新后的租约与独立 canvas 快照
     */
    static updatePreviewLease(lease: IImageEffectPreviewLease, layer: ILayerProps): Promise<IImageEffectPreviewResult> {
        return enqueueComplexRender(async () => {
            const renderLayer = cloneDeep(layer);
            const shouldRebind =
                complexBridgeOwner !== lease.token ||
                complexBridgeRevision !== lease.bridgeRevision ||
                !getBoundRenderSize(layer);

            if (shouldRebind) {
                await ImageEffectAction.glRenderUnsafe(renderLayer, lease.baseCanvas, true);
            } else {
                await ImageEffectAction.processRenderUnsafe(renderLayer, true, true);
            }
            complexBridgeOwner = lease.token;

            const { canvas } = await complexBridge.getGLResult();
            if (!canvas) {
                throw new Error(`Image effect preview result is empty: ${layer.id}`);
            }

            return {
                lease: {
                    ...lease,
                    bridgeRevision: complexBridgeRevision,
                },
                canvas: copyCanvas(canvas),
                effectPosition: cloneDeep((renderLayer.data as IImageLayerProps).effect?.effectPosition),
            };
        });
    }

    /**
     * 释放图片基础特效 GL 预览租约
     *
     * @param lease - 要释放的预览租约
     * @returns 当前租约是否仍持有全局 bridge
     */
    static releasePreviewLease(lease: IImageEffectPreviewLease | null) {
        if (!lease || complexBridgeOwner !== lease.token) return false;
        complexBridgeOwner = null;
        return true;
    }

    static transferLayerToLowLevelData(layer: ILayerProps) {
        const { data } = layer;
        const { effect } = data as IImageLayerProps;

        if (!effect) {
            return EmptyEffect;
        }

        const lowLevelData = [] as (MTIKComplexStrokeConfig | MTIKComplexGlowConfig | MTIKComplexShadowConfig)[];
        const { glow, shadow, stroke } = effect;

        // 处理描边效果，mComplexType = 2
        if (stroke.length !== 0 && !stroke[0].hidden) {
            const { width, color, strokeType } = stroke[0];
            const { r, g, b, a } = tinycolor(color).toRgb();
            const strokeData = {} as MTIKComplexStrokeConfig;

            strokeData.mComplexType = 2;
            strokeData.mColorR = r / 255;
            strokeData.mColorG = g / 255;
            strokeData.mColorB = b / 255;
            strokeData.mColorA = a;
            strokeData.mEditColorEnable = true;
            strokeData.mEditSizeEnable = true;
            strokeData.mEnable = width !== 0;
            strokeData.mLayerStyle = strokeType;
            strokeData.mIsRender = true;
            strokeData.mSize = width;
            lowLevelData.push(strokeData);
        } else {
            const strokeData = {} as MTIKComplexStrokeConfig;
            strokeData.mComplexType = 2;
            strokeData.mColorR = 0;
            strokeData.mColorG = 0;
            strokeData.mColorB = 0;
            strokeData.mColorA = 0;
            strokeData.mEditColorEnable = false;
            strokeData.mEditSizeEnable = false;
            strokeData.mEnable = false;
            strokeData.mIsRender = true;
            strokeData.mLayerStyle = 0;
            strokeData.mSize = 0;
            lowLevelData.push(strokeData);
        }
        // 处理发光效果，mComplexType = 1
        if (glow.length !== 0 && !glow[0].hidden) {
            const { width, color } = glow[0];
            const { r, g, b, a } = tinycolor(color).toRgb();
            const glowData = {} as MTIKComplexGlowConfig;

            glowData.mComplexType = 1;
            glowData.mBlur = width;
            glowData.mColorR = r / 255;
            glowData.mColorG = g / 255;
            glowData.mColorB = b / 255;
            glowData.mColorA = a;
            glowData.mEditColorEnable = true;
            glowData.mEditSizeEnable = width !== 0;
            glowData.mEnable = true;
            glowData.mLayerStyle = 0;
            glowData.mIsRender = true;
            lowLevelData.push(glowData);
        } else {
            const glowData = {} as MTIKComplexGlowConfig;
            glowData.mComplexType = 1;
            glowData.mBlur = 0;
            glowData.mColorR = 0;
            glowData.mColorG = 0;
            glowData.mColorB = 0;
            glowData.mColorA = 0;
            glowData.mEditColorEnable = false;
            glowData.mEditSizeEnable = false;
            glowData.mEnable = false;
            glowData.mIsRender = true;
            glowData.mLayerStyle = 0;
            lowLevelData.push(glowData);
        }

        // 处理阴影效果，mComplexType = 0
        if (shadow.length !== 0 && !shadow[0].hidden) {
            const { x, y, blur, color, type, rotate, direction, distance, wave, waveNum, brightness, opacity } =
                shadow[0];
            const { r, g, b, a } = tinycolor(color).toRgb();
            let shadowData;

            if (type === IImageEffectShadowType.base) {
                shadowData = {} as MTIKComplexShadowConfig;
                shadowData.mComplexType = 0;
                shadowData.mBlur = blur;
                shadowData.mColorR = r / 255;
                shadowData.mColorG = g / 255;
                shadowData.mColorB = b / 255;
                shadowData.mColorA = a;
                shadowData.mEditColorEnable = true;
                shadowData.mEditSizeEnable = true;
                shadowData.mEnable = true;
                shadowData.mLayerStyle = 0;
                shadowData.mIsRender = true;
                shadowData.mOffsetX = x;
                shadowData.mOffsetY = y;
                shadowData.mRotation = rotate || 0;
            } else if (type === IImageEffectShadowType.lightProjection) {
                shadowData = {} as MTIKComplexProjectionConfig;
                shadowData.mComplexType = 4;
                shadowData.mEditColorEnable = true;
                shadowData.mEditSizeEnable = true;
                shadowData.mEnable = true;
                shadowData.mLayerStyle = 0;
                shadowData.mIsRender = true;
                shadowData.mColorR = r / 255;
                shadowData.mColorG = g / 255;
                shadowData.mColorB = b / 255;
                shadowData.mColorA = a;
                shadowData.mBlur = blur;
                shadowData.mDirection = direction || 0;
                shadowData.mDistance = (distance || 0) / 100;
                shadowData.mOffsetY = -(y || 0) / 100;
            } else if (type === IImageEffectShadowType.reflection) {
                shadowData = {} as MTIKComplexProjectionConfig;
                shadowData.mComplexType = 4;
                shadowData.mEditColorEnable = true;
                shadowData.mEditSizeEnable = true;
                shadowData.mEnable = true;
                shadowData.mLayerStyle = MTIKProjectionType.MTIKProjectionTypeInverted;
                shadowData.mIsRender = true;
                shadowData.mBlur = blur;
                shadowData.mDistance = (distance || 0) / 100;
                shadowData.mBrightness = (brightness || 0) / 100;
                shadowData.mColorA = opacity || 1;
                shadowData.mOffsetX = (x || 0) / 100;
                shadowData.mOffsetY = -(y || 0) / 100;
            } else if (type === IImageEffectShadowType.waterSurface) {
                shadowData = {} as MTIKComplexProjectionConfig;
                shadowData.mComplexType = 4;
                shadowData.mEditColorEnable = true;
                shadowData.mEditSizeEnable = true;
                shadowData.mEnable = true;
                shadowData.mLayerStyle = MTIKProjectionType.MTIKProjectionTypeWater;
                shadowData.mIsRender = true;
                shadowData.mBlur = blur;
                shadowData.mWave = (wave || 0) / 100;
                shadowData.mNum = waveNum === undefined ? 10 : waveNum;
                shadowData.mColorA = opacity || 1;
                shadowData.mDistance = 1;
                shadowData.mOffsetY = -(y || 0) / 100;
            }
            lowLevelData.push(shadowData as any);

            if (type === IImageEffectShadowType.base) {
                lowLevelData.push({
                    mComplexType: 4,
                    mEnable: false,
                } as any);
            } else {
                lowLevelData.push({
                    mComplexType: 0,
                    mEnable: false,
                } as any);
            }
        } else {
            const shadowData = {} as MTIKComplexShadowConfig;
            shadowData.mComplexType = 0;
            shadowData.mBlur = 0;
            shadowData.mColorR = 0;
            shadowData.mColorG = 0;
            shadowData.mColorB = 0;
            shadowData.mColorA = 0;
            shadowData.mEditColorEnable = false;
            shadowData.mEditSizeEnable = false;
            shadowData.mEnable = false;
            shadowData.mIsRender = true;
            shadowData.mLayerStyle = 0;
            shadowData.mOffsetX = 0;
            shadowData.mOffsetY = 0;
            lowLevelData.push(shadowData);
            lowLevelData.push({
                mComplexType: 4,
                mEnable: false,
            } as any);
        }

        return lowLevelData;
    }

    static async loadClassicGlassAssets() {
        if (isLoadedAssets) return;
        await Promise.all([
            // 加载基础文字资源
            bgVirtualBridge.loadEffectZip(
                ClassicBlurFilterID,
                fixZipResUrl(assetsConfig.bgVirtual_newEffect_classBlur),
            ),
        ]);
        isLoadedAssets = true;
    }

    static async renderImageGlassBlur(layer: ILayerProps) {
        const { opacity, width, height, id } = layer;
        const {
            imageTransform: { widthRatio, heightRatio },
        } = layer.data as IImageLayerProps;

        const originImgWidth = toInteger(width * widthRatio);
        const originImgHeight = toInteger(height * heightRatio);
        const img = await loadImageAsync((layer.data as IImageLayerProps).url);
        const canvas = document.createElement('canvas');
        canvas.width = originImgWidth;
        canvas.height = originImgHeight;
        const ctx = canvas.getContext('2d');
        ctx?.drawImage(img, 0, 0, img.width, img.height, 0, 0, originImgWidth, originImgHeight);

        if (!canvas) {
            return;
        }
        const fromCanvas = createCanvasWasm(originImgWidth, originImgHeight, canvas);
        const defaultAlpha = await filterBridge.getDefaultAlpha(
            GlassBlurFilterID,
            fixZipResUrl(assetsConfig.filter_Uglass),
        );

        // 一些默认资源路径
        const AR_RES_ID = 'ar_res';
        const arResPath = assetsConfig.text_ar_res;
        const filter_path = 'filter/configuration_filter.plist';
        const ar_path = 'ar/configuration.plist';
        // 是否使用普通滤镜效果
        const isUseNormalEffect = false;
        // 是否使用 ar 效果
        const isUseArEffect = false; // true
        // 是否使用 mcp 效果
        const isUseMcpEffect = true; // false

        await filterBridge.from(fromCanvas).applyWithNoResult(
            GlassBlurFilterID, // 素材 id
            fixZipResUrl(assetsConfig.filter_Uglass), // 素材压缩包路径
            1, // 默认程度值
            filter_path, // 普通滤镜素材包路径(可忽略)
            ar_path, // ar滤镜素材包路径(可忽略)
            AR_RES_ID, // ar 素材包文件夹
            arResPath, // ar 资源包路径
            isUseArEffect, // 是否 ar 效果
            isUseMcpEffect, // 是否 mcp 效果
            true, // 是否需要初始化 view(首次加载需要)
        );
    }

    /**
     * 改变玻璃模糊模糊度
     *
     * @param alpha - 模糊度
     */
    static async changeImageGlassBlurAlpha(alpha: number) {
        // 7: 玻璃数量: 0-1 映射值 1-80
        // 8: 模糊程度: 0-1 映射值 0-1
        // 9: 偏移程度: 0-1 映射值 0-1
        await filterBridge.updateMcpSliderParam(alpha / 100, 7, true);
    }

    /**
     * 获取玻璃模糊结果图片链接地址
     */
    static async getImageGlassBlurResultUrl() {
        const resultCanvas = await ImageEffectAction.getImageGlassBlurResultCanvas();
        return resultCanvas ? ImageEffectAction.uploadImageResultCanvas(resultCanvas) : '';
    }

    /** @returns 与全局渲染桥解耦的玻璃模糊结果 Canvas */
    static async getImageGlassBlurResultCanvas() {
        const { canvas: resultCanvas } = await filterBridge.getGLResult();
        return resultCanvas ? copyCanvas(resultCanvas) : null;
    }

    static async renderClassicBlur(layer: ILayerProps, classicBlurParams: any) {
        await ImageEffectAction.loadClassicGlassAssets();
        const { opacity, width, height, id } = layer;
        const {
            imageTransform: { widthRatio, heightRatio },
        } = layer.data as IImageLayerProps;

        const originImgWidth = toInteger(width * widthRatio);
        const originImgHeight = toInteger(height * heightRatio);
        const img = await loadImageAsync((layer.data as IImageLayerProps).url);
        const canvas = document.createElement('canvas');
        canvas.width = originImgWidth;
        canvas.height = originImgHeight;
        const ctx = canvas.getContext('2d');
        ctx?.drawImage(img, 0, 0, img.width, img.height, 0, 0, originImgWidth, originImgHeight);

        if (!canvas) {
            return;
        }

        const fromCanvas = createCanvasWasm(originImgWidth, originImgHeight, canvas);
        await bgVirtualBridge
            .from(fromCanvas)
            .apply(
                ClassicBlurFilterID,
                fixZipResUrl(assetsConfig.bgVirtual_newEffect_classBlur),
                classicBlurParams,
                `${ClassicBlurFilterID}`,
                AR_RES_ID,
                fixZipResUrl(assetsConfig.text_ar_res),
                '',
                '',
                '',
                true,
                true,
            );
    }

    /**
     * 改变经典模糊模糊度
     *
     * @param alpha - 模糊度
     * @param classicBlurParams - 经典模糊参数
     */
    static async changeClassicBlurAlpha(alpha: number, classicBlurParams: any) {
        classicBlurParams.mFocalScale = alpha / 100;
        classicBlurParams.mUpdateEffect = classicBlurParams.mUpdateEffect + 1;
        bgVirtualBridge.setParams(classicBlurParams);
        await bgVirtualBridge.processRender();
    }

    /**
     * 获取经典模糊结果图片链接地址
     */
    static async getImageClassicBlurResultUrl() {
        const resultCanvas = await ImageEffectAction.getImageClassicBlurResultCanvas();
        return resultCanvas ? ImageEffectAction.uploadImageResultCanvas(resultCanvas) : '';
    }

    /** @returns 与全局渲染桥解耦的经典模糊结果 Canvas */
    static async getImageClassicBlurResultCanvas() {
        const { canvas: resultCanvas } = await bgVirtualBridge.getGLResult();
        return resultCanvas ? copyCanvas(resultCanvas) : null;
    }

    /**
     * 上传稳定的图片处理结果 Canvas
     *
     * @param canvas - 已与 WASM 全局渲染桥解耦的结果画布
     * @returns 上传后的图片地址
     */
    static async uploadImageResultCanvas(canvas: HTMLCanvasElement) {
        const { url } = await uploadBase64ToObs(canvas.toDataURL());
        return url;
    }
}

export default ImageEffectAction;
